import { Button, Group, ScrollArea, Stack } from '@mantine/core'
import { useDebouncedValue, useViewportSize } from '@mantine/hooks'
import { IconRefresh } from '@tabler/icons-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '@/components/ui-shared/InfiniteScroll/InfiniteScroll'
import { useGetDeviceLogsInfinite } from '@/queries/deviceQueries'
import { Camera } from '@/types/camera'
import { DeviceLog } from '@/types/device'
import { DeploymentAppLogsFilters } from './DeploymentAppLogsFilters'
import { DeploymentAppLogsTable } from './DeploymentAppLogsTable'
import { AppLogScope, buildScopeLogParams } from './buildGroupLogParams'
import {
  AppLogFilterState,
  DEFAULT_FILTERS,
  getStartDate
} from './deploymentAppLogsFilterConfig'

const SCROLL_OFFSET = 200

type DeploymentAppLogsTabProps = {
  scope: AppLogScope
  cameras?: Camera[]
  initialCamera?: string
}

export const DeploymentAppLogsTab = ({
  scope,
  cameras = [],
  initialCamera
}: DeploymentAppLogsTabProps) => {
  const { formatMessage } = useIntl()
  const { height } = useViewportSize()
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const [filters, setFilters] = useState<AppLogFilterState>(() => ({
    ...DEFAULT_FILTERS,
    camera: initialCamera
  }))
  const [debouncedSearch] = useDebouncedValue(filters.search, 400)
  // Memoized to avoid a new ISO string on every render, which would change the
  // query key and trigger an infinite refetch loop.
  const startDate = useMemo(
    () => getStartDate(filters.dateRange),
    [filters.dateRange]
  )

  const {
    data,
    isError,
    isFetching,
    isRefetching,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useGetDeviceLogsInfinite(
    {
      ...buildScopeLogParams(scope, filters.camera),
      ordering: '-timestamp',
      limit: 100,
      level: filters.level,
      start_date: startDate,
      search: debouncedSearch
    },
    { refetchInterval: 30_000 }
  )

  const members = scope.kind === 'group' ? scope.members : null

  const cameraNameOf = useCallback(
    (streamId: string) =>
      cameras.find((c) => c.camera_id === streamId)?.camera_name ?? streamId,
    [cameras]
  )

  const cameraOptions = useMemo(
    () =>
      members
        ? [...members]
            .sort((a, b) => a.stream_index - b.stream_index)
            .map((member) => ({
              value: member.deployment_id,
              label: cameraNameOf(member.stream_id)
            }))
        : undefined,
    [members, cameraNameOf]
  )

  const getCameraLabel = useMemo(() => {
    if (!members) {
      return undefined
    }
    return (log: DeviceLog) => {
      if (log.source_type === 'app_group') {
        return formatMessage({
          id: 'deployments.details.tabs.appLogs.columns.camera.group'
        })
      }
      if (log.source_type !== 'app_deployment') {
        return ''
      }
      const member = members.find((m) => m.deployment_id === log.deployment_id)
      return cameraNameOf(member?.stream_id ?? log.camera_id)
    }
  }, [members, cameraNameOf, formatMessage])

  const logs = useMemo(
    () => data?.pages.flatMap((p) => p.results) ?? [],
    [data?.pages]
  )

  const renderContent = () => {
    if (isError) {
      return (
        <ErrorWithReload onReload={() => void refetch()}>
          <FormattedMessage id="deployments.details.tabs.appLogs.error" />
        </ErrorWithReload>
      )
    }

    return (
      <ScrollArea
        h={height - SCROLL_OFFSET}
        viewportRef={scrollViewportRef}
        viewportProps={{ style: { paddingBottom: 10 } }}
      >
        <InfiniteScroll
          hasMore={hasNextPage}
          isFetching={isFetching}
          root={scrollViewportRef.current}
          onLoadMore={fetchNextPage}
        >
          <DeploymentAppLogsTable
            logs={logs}
            searchTerm={debouncedSearch ?? ''}
            getCameraLabel={getCameraLabel}
            isEmpty={!isFetching && logs.length === 0}
          />
        </InfiniteScroll>
      </ScrollArea>
    )
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <DeploymentAppLogsFilters
          filters={filters}
          cameraOptions={cameraOptions}
          onChange={setFilters}
        />
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isRefetching}
          onClick={() => void refetch()}
        >
          <FormattedMessage id="reload" />
        </Button>
      </Group>

      {renderContent()}
    </Stack>
  )
}
